<template>
  <div>
    <!-- Confirmation Modal -->
    <ConfirmationModal
      :title="confirmationTitle"
      :message="confirmationMessage"
      confirmColor="error darken-1"
      ref="confirmationModal"
    />

    <!-- Income Modal-->
    <v-dialog
      width="480px"
      :overlay-opacity="0.90"
      v-model="incomeDialog"
    >
      <v-card>
        <v-card-title class="d-block">
          <div class="font-weight-bold">{{item.id ? "Update" : "Add"}} Income</div>
          <p class="caption mb-4">Enter income details below.</p>
        </v-card-title>
        <v-card-text class="py-2">

          <v-form
            @submit.prevent="addItem()"
          >
            <ValidationObserver ref="validationObserver">

              <ValidationProvider
                name="Income Type"
                :rules="'required'"
                v-slot="{ errors }"
              >
                <v-select
                  outlined
                  dense
                  :autofocus="true"
                  label="Income Type"
                  placeholder="Select income type"
                  :items="incomeTypes"
                  item-text="accountName"
                  item-value="accountName"
                  v-model="item.accountName"
                  :error-messages="errors"
                >
                  <template v-slot:label>
                    Income Type
                    <span class="red--text">*</span>
                  </template>
                </v-select>
              </ValidationProvider>

              <ValidationProvider
                name="Source"
                :rules="'required:max:400'"
                v-slot="{ errors }"
              >
                <v-text-field
                  outlined
                  dense
                  label="Source of Income"
                  v-model="item.source"
                  :error-messages="errors"
                >
                  <template v-slot:label>
                    Source of Income
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
              <ValidationProvider
                name="Amount"
                :rules="'required|min_value:0|max_value:999999999999'"
                v-slot="{ errors }"
              >
                <vuetify-money
                  type="number"
                  outlined
                  hide-details
                  dense
                  required
                  label="Amount"
                  v-model="item.amount"
                  :error-messages="errors"
                  :options="VuetifyMoneyOptions"
                />
              </ValidationProvider>
            </ValidationObserver>

            <v-card-actions class="pt-5 justify-end">
              <v-btn
                @click="closeModal()"
                min-width="120"
                min-height="42"
                depressed
                class="text-capitalize"
              >Cancel</v-btn>
              <v-btn
                type="submit"
                min-width="120"
                min-height="42"
                depressed
                color="primary"
                class="text-capitalize"
              >Save</v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-row
      align="center"
      justify="space-between"
    >
      <v-col
        cols="12"
        md="5"
      >
        <h3>Income</h3>
      </v-col>
      <v-col
        cols="12"
        md="6"
        class="text-md-right"
      >
        <v-btn
          @click="openModal()"
          class="text-capitalize"
          depressed
          color="primary"
        >Add Income</v-btn>
      </v-col>
    </v-row>
    <v-card
      class="mt-5"
      outlined
      rounded="lg"
    >
      <v-data-table
        :items="incomes"
        hide-default-footer
        :headers="headers"
      >
        <!-- Customized No Data slot -->
        <template #no-data>
          <v-row justify="center">
            <v-col>
              <NoData />
              <p class="text-center mt-5">No Income Items</p>
            </v-col>
          </v-row>
        </template>
        <template #[`item.accountName`]="{ item }">
          <span class="text-capitalize">{{item.accountName}}</span>
        </template>
        <template #[`item.source`]="{ item }">
          <span class="text-capitalize">{{item.source}}</span>
        </template>
        <template #[`item.amount`]="{ item }">
          <span class="text-capitalize">{{asMoney(item.amount)}}</span>
        </template>

        <!-- Action slot -->
        <template #[`item.actions`]="{ item }">
          <v-btn
            @click="openModal(item)"
            class="text-capitalize"
            text
            color="primary"
          >
            Edit</v-btn>
          <v-btn
            @click="removeItem(item)"
            class="text-capitalize"
            text
            color="error"
          >
            Remove</v-btn>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import PageMixin from '@/mixins/page.mixin';
import { VuetifyMoneyOptions } from '@/lib/constants/options';
import { cloneDeep } from 'lodash';
import { v4 } from 'uuid';
export default {
  mixins: [PageMixin],
  data() {
    return {
      /** @type {import('vuetify').DataTableHeader[]}*/
      headers: [
        { text: 'Income', value: 'accountName', sortable: false },
        { text: 'Source of Income', value: 'source', sortable: false },
        { text: 'Amount', value: 'amount', sortable: false },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false }
      ],  
      VuetifyMoneyOptions: VuetifyMoneyOptions,
      confirmationTitle: "",
      confirmationMessage: "",
      incomeDialog: false,
      item: {},
    };
  },
  props: {
    incomes: {
      type: Array,
      default: () =>([])
    },
    incomeTypes: {
      type: Array,
      default: () =>([])
    } 
  },
  methods: {
    openModal(item) {
      this.$refs.validationObserver?.reset();
      this.item = cloneDeep(item) || {};
      this.incomeDialog = true;
    },

    closeModal(item) {
      this.incomeDialog = false;
      this.item = item || {};
      this.$refs.validationObserver?.reset();
    },

    async addItem() {
      const incomes = cloneDeep(this.incomes)
      const valid = await this.$refs.validationObserver.validate();
      if(!valid) return;
      
      if(this.item.id) {
        const index = incomes.findIndex((el) => el.id == this.item.id)
        incomes.splice(index, 1, {...this.item});

        this.$emit('update', incomes) 
        this.closeModal();
        return;
      }
      this.item.id = v4(); 
      incomes.push(this.item);
      this.$emit('update', incomes) ;
      this.closeModal();
    },
  
    removeItem(item) {
      this.confirmationTitle = `Confirm Deletion`;
      this.confirmationMessage = `Are you sure you want to delete income type '${item.accountName}'?`;
      this.$refs.confirmationModal.show(async () => {
        const incomes = this.incomes.filter((el) => el.id !== item.id)
        this.$emit('update', incomes) 
      });
    }
  }
};
</script>
>
